// Terrain from Terrarium-encoded PNG tiles (AWS Terrain Tiles, or self-hosted with the same format).

import { lngLatToTile, pixelSize, type GridSpec, type LngLat } from '../lib/geo';
import type { TerrainSource } from './types';

const TILE = 256;

/** Terrarium: elevation = R * 256 + G + B / 256 - 32768. */
export function decodeTerrarium(rgba: ArrayLike<number>): Float32Array {
  const out = new Float32Array(rgba.length / 4);
  for (let i = 0, j = 0; j < out.length; i += 4, j++) {
    out[j] = rgba[i] * 256 + rgba[i + 1] + rgba[i + 2] / 256 - 32768;
  }
  return out;
}

/** Smallest zoom whose pixels are at most half a cell (capped at `maxzoom`). */
export function zoomForResolution(lat: number, metres: number, maxzoom: number): number {
  for (let z = 0; z <= maxzoom; z++) if (pixelSize(lat, z) <= metres / 2) return z;
  return maxzoom;
}

/** Fetches a tile and returns RGBA pixels, or null if missing. Injected so Node can decode PNGs. */
export type TileLoader = (url: string) => Promise<Uint8ClampedArray | null>;

/** Browser / Web Worker loader using createImageBitmap + OffscreenCanvas. */
export const browserTileLoader: TileLoader = async (url) => {
  const res = await fetch(url);
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) throw new Error(`terrain tile ${res.status}`);
  const bmp = await createImageBitmap(await res.blob(), {
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
  });
  const canvas = new OffscreenCanvas(bmp.width, bmp.height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  return ctx.getImageData(0, 0, TILE, TILE).data;
};

export class TerrariumSource implements TerrainSource {
  readonly id = 'terrarium';
  readonly attribution: string;
  readonly dem;
  private tiles = new Map<string, Promise<Float32Array>>();
  private order: string[] = [];

  constructor(
    private url: string,
    private maxzoom: number,
    attribution: string,
    private loader: TileLoader = browserTileLoader,
    private cacheSize = 96,
  ) {
    this.attribution = attribution;
    this.dem = { tiles: [url], encoding: 'terrarium' as const, tileSize: TILE, maxzoom: maxzoom };
  }

  private tile(z: number, x: number, y: number): Promise<Float32Array> {
    const n = 2 ** z;
    x = ((x % n) + n) % n;
    y = Math.min(Math.max(y, 0), n - 1);
    const key = `${z}/${x}/${y}`;
    let t = this.tiles.get(key);
    if (!t) {
      const url = this.url.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
      t = this.loader(url).then((px) => (px ? decodeTerrarium(px) : new Float32Array(TILE * TILE)));
      t.catch(() => this.tiles.delete(key));
      this.tiles.set(key, t);
      this.order.push(key);
      while (this.order.length > this.cacheSize) this.tiles.delete(this.order.shift()!);
    }
    return t;
  }

  /** Load every tile needed to sample `points` at zoom z, then return a synchronous sampler. */
  private async sampler(points: LngLat[] | { minX: number; maxX: number; minY: number; maxY: number }, z: number, onProgress?: (d: number, t: number) => void) {
    let minX: number, maxX: number, minY: number, maxY: number;
    if (Array.isArray(points)) {
      minX = minY = Infinity;
      maxX = maxY = -Infinity;
      for (const p of points) {
        const t = lngLatToTile(p, z);
        minX = Math.min(minX, t.x);
        maxX = Math.max(maxX, t.x);
        minY = Math.min(minY, t.y);
        maxY = Math.max(maxY, t.y);
      }
    } else ({ minX, maxX, minY, maxY } = points);
    // one pixel of slack for bilinear neighbours
    const tx0 = Math.floor(minX - 1 / TILE);
    const tx1 = Math.floor(maxX + 1 / TILE);
    const ty0 = Math.floor(minY - 1 / TILE);
    const ty1 = Math.floor(maxY + 1 / TILE);
    const keys: [number, number][] = [];
    for (let y = ty0; y <= ty1; y++) for (let x = tx0; x <= tx1; x++) keys.push([x, y]);
    let done = 0;
    const loaded = await Promise.all(
      keys.map(([x, y]) =>
        this.tile(z, x, y).then((t) => {
          onProgress?.(++done, keys.length);
          return t;
        }),
      ),
    );
    const w = tx1 - tx0 + 1;
    const px = (gx: number, gy: number) => {
      const tx = Math.floor(gx / TILE);
      const ty = Math.floor(gy / TILE);
      const t = loaded[(Math.min(Math.max(ty, ty0), ty1) - ty0) * w + (Math.min(Math.max(tx, tx0), tx1) - tx0)];
      const lx = Math.min(Math.max(gx - tx * TILE, 0), TILE - 1);
      const ly = Math.min(Math.max(gy - ty * TILE, 0), TILE - 1);
      return t[ly * TILE + lx];
    };
    return (p: LngLat) => {
      const t = lngLatToTile(p, z);
      // pixel centres sit at +0.5
      const fx = t.x * TILE - 0.5;
      const fy = t.y * TILE - 0.5;
      const x0 = Math.floor(fx);
      const y0 = Math.floor(fy);
      const ax = fx - x0;
      const ay = fy - y0;
      const a = px(x0, y0);
      const b = px(x0 + 1, y0);
      const c = px(x0, y0 + 1);
      const d = px(x0 + 1, y0 + 1);
      return (a + (b - a) * ax) * (1 - ay) + (c + (d - c) * ax) * ay;
    };
  }

  async elevations(points: LngLat[], opts: { resolution?: number } = {}): Promise<Float32Array> {
    if (points.length === 0) return new Float32Array(0);
    const z = zoomForResolution(points[0][1], opts.resolution ?? 30, Math.min(this.maxzoom, 13));
    const s = await this.sampler(points, z);
    return Float32Array.from(points, s);
  }

  async grid(spec: GridSpec, onProgress?: (done: number, total: number) => void): Promise<Float32Array> {
    const z = zoomForResolution(spec.lat0, spec.cell, Math.min(this.maxzoom, 13));
    const nw = lngLatToTile([spec.lng0 - (spec.cx + 1) * spec.dLng, spec.lat0 + (spec.cy + 1) * spec.dLat], z);
    const se = lngLatToTile([spec.lng0 + (spec.nx - spec.cx) * spec.dLng, spec.lat0 - (spec.ny - spec.cy) * spec.dLat], z);
    const sample = await this.sampler({ minX: nw.x, maxX: se.x, minY: nw.y, maxY: se.y }, z, onProgress);
    const out = new Float32Array(spec.nx * spec.ny);
    for (let r = 0; r < spec.ny; r++) {
      const lat = spec.lat0 - (r - spec.cy) * spec.dLat;
      for (let c = 0; c < spec.nx; c++) {
        out[r * spec.nx + c] = sample([spec.lng0 + (c - spec.cx) * spec.dLng, lat]);
      }
    }
    return out;
  }
}
